// This file provides some sitecore utilities
// Even though `getPublicUrl` is exposed by Sitecore, it throws an exception when called from middleware
// due to dependency on `chalk` library to colored console.warn.
import { Field, LayoutServiceContext } from '@sitecore-jss/sitecore-jss-nextjs';
import {
  ComponentRendering,
  GenericFieldValue,
} from '@sitecore-jss/sitecore-jss/types/layout/models';
import { BoolOrStringType } from 'lib/util/strings';
import { isTruthy } from 'lib/util/urls';
import { env } from 'lib/env';
import {
  asLayoutServiceData,
  isComponentRenderingWithPlaceholders,
  isNamedComponentRendering,
} from './type-guards';
import { logWarning } from 'lib/util/errors';

// For `getJssEditingSecret` this isn't exposed publicly in the library

/**
 * Get the publicUrl based on environment variable.  We cannot use VERCEL_URL because it doesn't account for the
 * canonical url and instead uses vercel's auto generated url.
 */
export const getRealPublicUrl = (): string => {
  // Don't use the VERCEL_URL because that returns the temp url that gets auto created
  let url = env.PUBLIC_URL;
  // Zod should make this impossible, but just in case
  if (url === undefined) {
    logWarning(
      'Warning: An PUBLIC_URL environment variable is not defined. Falling back to http://localhost:3000.'
    );
    url = 'http://localhost:3000';
  } else {
    // Zod should also make this impossible, but just in case
    try {
      new URL(url);
    } catch (error) {
      throw new Error(`The PUBLIC_URL environment variable '${url}' is not a valid URL.`);
    }
  }
  // Zod should -also- be handling this for us, but just in cse everythihng has gone awry
  return url.toString().replace(/\/$/, '');
};
/**
 * Typeguard-esque function to determine if the input is a wildcard item
 * @param input Value to evaluate.
 * @returns {boolean} true if the input is a wildcard item
 */
export const asWildcardRoute = (input: unknown): LayoutServiceContext | undefined => {
  const ctx = asLayoutServiceData(input);
  if (!ctx?.wcPath) {
    return undefined;
  }
  return ctx;
};
export const getJssEditingSecret = (): string => {
  const secret = process.env.JSS_EDITING_SECRET;
  if (!secret || secret.length === 0) {
    throw new Error('The JSS_EDITING_SECRET environment variable is missing or invalid.');
  }
  return secret;
};
export const getHostName = (url: string): string => {
  const tempUrl = new URL(url);
  return tempUrl.host;
};
/**
 * Determines whether or not input is truthy
 * @param input value to check
 * @returns true if input is truty
 */
export const getTruthyFieldValue = (
  input?: Field | GenericFieldValue | BoolOrStringType
): boolean => {
  if (typeof input !== 'object') {
    return isTruthy(input);
  }
  const field = 'value' in input ? input.value : input;
  return isTruthy(field);
};

/**
 * Recursively searches for a component rendering with the specified component name
 * within the given rendering object. If found, returns the matching component rendering.
 *
 * @template TComponentName - The type of the component name.
 * @param rendering - The rendering object to search within. This can be a named component rendering
 * or a component rendering with placeholders.
 * @param componentName - The name of the component to search for.
 * @returns A component rendering object with the specified component name, or `undefined` if no match is found.
 *
 * The function first checks if the provided rendering matches the specified component name
 * using `isNamedComponentRendering`. If not, it checks if the rendering contains placeholders
 * using `isComponentRenderingWithPlaceholders`. If placeholders are present, it recursively
 * searches through the child renderings within those placeholders.
 */
export const findComponentInRendering = <TComponentName extends string>(
  rendering: unknown,
  componentName: TComponentName
): (ComponentRendering & { componentName: TComponentName }) | undefined => {
  if (isNamedComponentRendering(rendering, componentName)) {
    return rendering;
  }
  if (isComponentRenderingWithPlaceholders(rendering)) {
    const keys = Object.keys(rendering.placeholders);
    for (let idxPlaceholders = 0; idxPlaceholders < keys.length; idxPlaceholders++) {
      const placeholder = rendering.placeholders[keys[idxPlaceholders]];
      for (let idxComponents = 0; idxComponents < placeholder.length; idxComponents++) {
        const childRendering = findComponentInRendering(placeholder[idxComponents], componentName);
        if (childRendering) {
          return childRendering;
        }
      }
    }
  }
  return undefined;
};
